import React, { useState, useEffect, useRef } from 'react';
import { TrashIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { black } from 'tailwindcss/colors';

const UseApp = () => {
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const nameInputRef = useRef(null);

    // Fetch links from the API
    const fetchLinks = async () => {
        try {
            const response = await axios.get('https://visitguru.in/api/v1/link/get');
            if (response.data.success) {
                // Sort links by creation time or another field if available
                const fetchedData = response.data.data.sort((a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt)
                );
                // Map title to name for UI consistency
                const formattedData = fetchedData.map(item => ({
                    ...item,
                    name: item.title, // Use title from backend as name in UI
                }));
                setTableData(formattedData);
            } else {
                toast.error('Failed to fetch links');
            }
        } catch (err) {
            console.error('Failed to fetch links:', err.message);
            toast.error('Failed to fetch links');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLinks();
    }, []);

    // Handle form submission to add a new link
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name || !url) {
            toast.warning('Please enter a title and URL');
            return;
        }

        try {
            const response = await axios.post('https://visitguru.in/api/v1/link/add', {
                title: name, // Send name as title to match backend
                link: url,
            }, {
                headers: { 'Content-Type': 'application/json' },
            });

            if (response.data.success) {
                await fetchLinks();
                setName('');
                setUrl('');
                toast.success('Link added successfully');
                if (nameInputRef.current) nameInputRef.current.value = null;
            } else {
                toast.error('Failed to add link');
            }
        } catch (error) {
            console.error('Add link failed:', error.message);
            toast.error('Failed to add link. Please try again.');
        }
    };

    // Handle link deletion
    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(`https://visitguru.in/api/v1/link/delete/${id}`);
            if (response.data.success) {
                await fetchLinks();
                toast.success('Deleted successfully');
            } else {
                toast.error('Failed to delete link');
            }
        } catch (error) {
            console.error('Delete failed:', error.message);
            toast.error('Failed to delete. Please try again.');
        }
    };

    return (
        <div className="space-y-12">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
            <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
                <div className="rounded-t px-4 py-3 border-0 bg-blue-600 text-white">
                    <h2 className="text-lg font-semibold" style={{ color: black }}>How to Use Vendor</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 p-4">
                    <div className="flex flex-col md:flex-row items-center gap-x-4 w-full">
                        <div className="flex flex-col w-full md:w-1/3 m-2">
                            <label className="text-sm font-medium text-gray-700 mb-1">Enter Title</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter title"
                                ref={nameInputRef}
                                className="border border-gray-300 rounded px-3 py-2"
                            />
                        </div>

                        <div className="flex flex-col w-full md:w-1/2">
                            <label className="text-sm font-medium text-gray-700 mb-1">Enter URL</label>
                            <input
                                type="text"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://example.com"
                                className="border border-gray-300 rounded px-3 py-2"
                            />
                        </div>

                        <div className="flex items-end pt-6">
                            <button
                                type="submit"
                                style={{
                                    backgroundColor: '#0884c4',
                                    width: '120px',
                                    height: '42px',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    marginLeft: '10px',
                                }}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </form>

                {/* Table */}
                <div className="overflow-x-auto px-4 pb-6">
                    {loading ? (
                        <div>Loading...</div>
                    ) : (
                        <table className="w-full table-auto border rounded mt-4">
                            <thead className="bg-gray-100 text-gray-700 text-left">
                                <tr>
                                    <th className="px-4 py-2 border">#</th>
                                    <th className="px-4 py-2 border">Name</th>
                                    <th className="px-4 py-2 border">URL</th>
                                    <th className="px-4 py-2 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.length > 0 ? (
                                    tableData.map((item, index) => (
                                        <tr key={item._id} className="text-left border-b hover:bg-gray-50">
                                            <td className="px-4 py-2 border">{index + 1}</td>
                                            <td className="px-4 py-2 border">{item.name}</td>
                                            <td className="px-4 py-2 border break-all">
                                                <a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 underline"
                                                >
                                                    {item.link}
                                                </a>
                                            </td>
                                            <td className="px-4 py-2 border">
                                                <button
                                                    onClick={() => handleDelete(item._id)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <TrashIcon className="w-5 h-5 inline" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4 text-gray-500">
                                            No records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UseApp;